import type { Migration } from '../types';
import { isInFlightStatus } from './migrationStatus';

/**
 * Default poll cadence when at least one job is still running (queued or in-progress
 * across any of export / transform / import / rollback). A tighter interval keeps the
 * dashboard and progress bars feeling live so long-running jobs never look "stuck".
 */
export const ACTIVE_POLLING_INTERVAL_MS = 10000;

/**
 * Default poll cadence when nothing is in flight. A slower interval avoids needless
 * traffic while the dashboard is quiescent; a manual refresh or a new job restores
 * the faster cadence on the next fetch.
 */
export const IDLE_POLLING_INTERVAL_MS = 30000;

export interface PollingIntervalOptions {
    activeIntervalMs?: number;
    idleIntervalMs?: number;
}

/**
 * Decides how often the jobs list should be re-fetched based on whether any job is
 * currently in flight. Pure and side-effect free so it can be unit tested and reused
 * by the polling hook.
 *
 * @param jobs the current list of migrations (may be empty/undefined)
 * @param options optional overrides for the active/idle cadences
 * @returns the poll interval in milliseconds
 */
export function computePollingInterval(
    jobs: readonly Migration[] | undefined | null,
    options: PollingIntervalOptions = {}
): number {
    const activeIntervalMs = options.activeIntervalMs ?? ACTIVE_POLLING_INTERVAL_MS;
    const idleIntervalMs = options.idleIntervalMs ?? IDLE_POLLING_INTERVAL_MS;

    if (!jobs || jobs.length === 0) {
        return idleIntervalMs;
    }

    const anyInFlight = jobs.some((job) => isInFlightStatus(job.status));
    return anyInFlight ? activeIntervalMs : idleIntervalMs;
}
